interface CouplePairProps {
  /** Both partners' first names. On S11, the READER first: the second disc is the
   *  one that can be an empty seat. */
  names: string[];
  /**
   * Whether the second partner has arrived. Defaults to true, which is the only
   * answer S1 can give: it runs before the self-select, so nobody is bound and
   * there is no "other" yet to be waiting on.
   */
  otherJoined?: boolean;
  /**
   * What the pair sits ON. `surface` (default): inside a card, as on S1/S11, so the
   * discs are --canvas pressed into it. `canvas`: straight on the page (the Homebase
   * header), where --canvas discs would vanish into the ground, so the discs flip to
   * the card colour with the card's hairline, and the empty seat to the page colour.
   */
  ground?: "surface" | "canvas";
  /** `lg` (default) 64px: the hero of an onboarding screen. `md` 48px: in a page
   *  header, beside a title rather than above one. */
  size?: "md" | "lg";
}

/** One disc. 64px, on --canvas rather than the sage --interactive-soft.
 *
 *  --canvas is the ground token, one step off the --surface card, so the discs read
 *  as a shape pressed into the card rather than as something coloured sitting on it.
 *  That is deliberate at this size: sage at 64px started to read as a status colour,
 *  and sage is the interactive family (focus rings, checked radios, selected cards)
 *  on a screen where these two are the only thing you CANNOT touch. --cta-ink on
 *  --canvas is 14.6:1, so the initials carry the contrast the fill gives up.
 *
 *  --type-h2 (24px), up from h3 with the disc: h3's 18px was 37% of a 48px disc and
 *  28% of a 64px one, so holding it would have read as the letters shrinking while
 *  the circles grew. 24 of 64 restores the original proportion exactly. */
// Per size: the disc box, the initials (h2 24px of 64 and h3 18px of 48 hold the same
// ~37% proportion), the overlap (a quarter of the disc), the seat's plus icon and the
// dashed ring's viewBox (tracks the disc so its stroke stays 1px on screen).
const SIZE = {
  lg: { box: "h-16 w-16", type: "[font:var(--type-h2)]", overlap: "-ml-4", px: 64, icon: 24 },
  md: { box: "h-12 w-12", type: "[font:var(--type-h3)]", overlap: "-ml-3", px: 48, icon: 18 },
} as const;

const GROUND = {
  surface: { disc: "bg-[var(--canvas)]", ring: "ring-[var(--surface)]", seat: "bg-[var(--surface)]" },
  canvas: {
    disc: "bg-[var(--surface)] border border-[var(--border)]",
    ring: "ring-[var(--canvas)]",
    seat: "bg-[var(--canvas)]",
  },
} as const;

/** The overlap is what makes them a pair rather than a list, held at a quarter of
 *  the disc as it grew (12px of 48, 16px of 64). The ring is the card's own colour,
 *  so the disc in front cuts a clean edge out of the one behind instead of tinting
 *  it. */

/**
 * The two of them, as two overlapping initial discs.
 *
 * WHY IT IS NOT ONE OF THE AVATAR PAIRS THAT ALREADY EXISTED. S11 drew "you, plus a
 * seat for your partner" in its own local `PairedAvatars`: 48px, sage fill, its own
 * dashed circle. S1 drew two equal 64px discs on --canvas. Two treatments of one
 * idea, three screens apart in the same flow, differing in size, fill and type.
 *
 * They are ONE component now, and the empty seat came here rather than the styling
 * going there: the seat is a STATE of this pair, not a different pair. What it must
 * not lose is the meaning — a dashed second disc says the other partner has not
 * arrived, and on S11 it sits directly above "Invite Chris". Rendering two filled
 * discs there would have the picture claim he had joined while the button under it
 * asked you to invite him.
 *
 * Both discs are identical and neither is labelled when both are in. That is the
 * point on S1: the screen's job is to answer "is this about us?" before it asks for
 * anything, and two equal marks say it faster than the sentence underneath does. S1
 * cannot say which one is you, so it does not try — rendering one as filled would be
 * a guess, and a wrong guess on the first screen is worse than no avatar at all.
 *
 * Degrades quietly, and by STATE: with both discs drawn, fewer than two names (a
 * roster still loading, a couple with one member) renders nothing rather than a lone
 * disc, which would say the opposite of what it is here to say. With a seat waiting,
 * one name is enough, because the seat carries no letter of its own.
 */
export function CouplePair({
  names,
  otherJoined = true,
  ground = "surface",
  size = "lg",
}: CouplePairProps) {
  const dim = SIZE[size];
  const DISC = `flex ${dim.box} items-center justify-center rounded-full ${dim.type} text-[var(--cta-ink)] ${GROUND[ground].disc}`;
  const OVERLAP = `${dim.overlap} ring-2 ${GROUND[ground].ring}`;
  const SEAT = GROUND[ground].seat;
  const initials = names
    .map((n) => n.trim().charAt(0).toUpperCase())
    .filter(Boolean);

  // The SECOND initial is only required when it is actually drawn. In the pending
  // state the second disc is an empty seat and carries no letter, so demanding one
  // there threw away the whole pair — including the reader's own disc and the seat
  // that pairs with "Invite <name>" — whenever a partner row had a blank first name.
  // (`otherName` on S11 is `other?.firstName ?? "your partner"`, and `??` does not
  // catch an empty string.)
  if (initials.length === 0) return null;
  if (otherJoined && initials.length < 2) return null;

  return (
    // Decorative: the names are already on the screen for anyone reading it, and
    // "M C" announced as text is noise in a screen reader.
    <div className="flex items-center" aria-hidden="true">
      <span className={DISC}>{initials[0]}</span>
      {otherJoined ? (
        <span className={`${DISC} ${OVERLAP}`}>{initials[1]}</span>
      ) : (
        // THE EMPTY SEAT. The GROUND's own colour (--surface in a card, --canvas on the
        // page), not the disc's: it has to read as a gap in what it sits on rather
        // than as a second disc that happens to be blank, which is the
        // whole difference between "waiting for them" and "here they are".
        <span
          className={`relative flex ${dim.box} items-center justify-center rounded-full ${SEAT} text-[var(--text-tertiary)] ${OVERLAP}`}
        >
          {/* Dashed ring — 1px stroke, 2px dash / 2px gap. CSS borders cannot
              control dash length, so it is drawn as an SVG circle. The viewBox
              tracks the disc (64, r 31.5) so the stroke stays 1px on screen
              instead of scaling up with the shape. */}
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox={`0 0 ${dim.px} ${dim.px}`}
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx={dim.px / 2}
              cy={dim.px / 2}
              r={dim.px / 2 - 0.5}
              stroke="var(--text-tertiary)"
              strokeWidth="1"
              strokeDasharray="2 2"
            />
          </svg>
          {/* 24px, holding the 20-of-48 proportion the seat had at the smaller
              size, and landing on the DS icon grid rather than between it. */}
          <svg
            width={dim.icon}
            height={dim.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      )}
    </div>
  );
}
